/* eslint-disable no-underscore-dangle */
import React, { useMemo, useState } from 'react';

import { useWindowSize } from '@src/hooks/useWindowSize';
import { useQueryUserConfig } from '@src/queries/users/useQueryUserConfig';
import NavBar from 'components/NavBar';
import DeleteIcon from '@mui/icons-material/Delete';
import LockIcon from '@mui/icons-material/Lock';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import { Tooltip } from '@mui/material';

import { setPopup } from '@src/state/popupAtoms';
import { EPopup } from '@src/types/common';
import {
  Pathname, Browsing, ArgsName, PathAction,
} from '@src/types/Browsing';
import { LocalizedStrings } from '@src/utils/localizedStrings';
import { Services, ServiceName } from '@src/types/Services';
import { style } from 'typestyle';
import { ActionButton } from '@src/components/ActionButton';
import { Hide } from '@src/components/Hide';
import { FormError } from '@src/components/Form/FormError';
import { WidgetView } from 'common';
import { Footer } from '@src/components/Footer';
import { ADALONG_COLORS } from '@src/styles';
import { useMutationRemoveWidget } from '../../queries/widgets/useMutationRemoveWidget';
import { useQueryWidgets } from '../../queries/widgets/useQueryWidgets';
import AuthorizedPage from '../../components/AuthorizedPage';
import { TableList, ListColumn } from '../../components/TableList';
import { getSourceType } from './utils/sourceType';
import { getSourceNames } from './utils/sourceNames';
import { useMutationChangeWidgetLockStatus } from '@src/queries/widgets/useMutationChangeWidgetLockStatus';

type SortKey = 'name' | 'creator' | 'type' | 'live';
type SortDir = 'asc' | 'desc';

interface OwnProps {
  services: Services
}

type Props = OwnProps;

function Widgets(props: Props) {
  const { height: windowHeight } = useWindowSize();
  const browsingService: Browsing = props.services.get(ServiceName.Browsing);

  const { data: userConfig } = useQueryUserConfig();
  const user = userConfig?.user ?? null;

  const {
    data: widgets = [],
    isLoading,
    error: errorLoading,
  } = useQueryWidgets();

  const {
    mutate: removeWidget,
    error: errorRemoving,
  } = useMutationRemoveWidget();

  const {
    mutate: changeWidgetLockStatus,
    error: errorLocking,
  } = useMutationChangeWidgetLockStatus();

  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [userSorted, setUserSorted] = useState(false);

  const hasProductCatalog = Boolean(user?.groupFeatures.product_catalog);

  const handleSort = (key: SortKey) => {
    setUserSorted(true);
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const displayedWidgets = useMemo(() => {
    const list = widgets.filter((w) => {
      if (!hasProductCatalog && getSourceType(w) === 'product') return false;
      return true;
    });
    return [...list].sort((a, b) => {
      // Live-first only when the user hasn't explicitly chosen a sort
      if (!userSorted) {
        const aLive = a.live_status?.domain ? 0 : 1;
        const bLive = b.live_status?.domain ? 0 : 1;
        if (aLive !== bLive) return aLive - bLive;
      }

      if (sortKey === 'live') {
        const aLive = a.live_status?.domain ? 0 : 1;
        const bLive = b.live_status?.domain ? 0 : 1;
        return sortDir === 'asc' ? aLive - bLive : bLive - aLive;
      }

      const getVal = (w: WidgetView) => {
        if (sortKey === 'name') return w.name;
        if (sortKey === 'type') return getSourceType(w);
        return w.created_by.displayName ?? '';
      };
      const cmp = getVal(a).toLowerCase().localeCompare(getVal(b).toLowerCase());
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [widgets, sortKey, sortDir, userSorted, hasProductCatalog]);

  const onRemoveWidget = (widget: WidgetView) => {
    setPopup(EPopup.promptconfirm, {
      question: lang.confirmDelete,
      text: { yes: lang.delete, no: lang.cancel },
      yesColor: 'red',
      answer: async (yes) => {
        if (!yes) return;
        return removeWidget({ id: widget._id });
      },
    });
  };

  const onWidgetClick = (widget: WidgetView) => {
    browsingService.goToPage({
      root: Pathname.widget,
      args: new Map().set(ArgsName.widget, widget._id),
    });
  };

  const onWidgetLockClick = (input: WidgetView) => {
    const isOwner = input.created_by._id === user?._id;
    if (!isOwner) return;
    if (input.is_locked) {
      setPopup(EPopup.promptconfirm, {
        question: lang.confirmUnlockWidget,
        text: { yes: lang.unlock, no: lang.cancel },
        yesColor: 'blue',
        answer: async (yes) => {
          if (!yes) return;
          return changeWidgetLockStatus({ id: input._id, widget: input });
        },
      });
      return;
    }
    changeWidgetLockStatus({ id: input._id, widget: input });
  };

  const renderTypeHeader = () => renderSortHeader('type', lang.type);

  // Budget per pill line ≈ 300px; each char ≈ 7.5px + 24px pill overhead
  const pillLineBudget = 290;
  const pillCharPx = 7.5;
  const pillOverheadPx = 24;
  const overflowPillPx = 3 * pillCharPx + pillOverheadPx; // "•••"

  const slicePills = (names: string[], maxLines: number): { shown: string[]; hasMore: boolean } => {
    const budgetTotal = pillLineBudget * maxLines;
    let used = 0;
    const shown: string[] = [];
    for (const name of names) {
      const pillPx = name.length * pillCharPx + pillOverheadPx;
      const remaining = budgetTotal - used;
      // Reserve space for overflow pill if more names are left after this one
      const needsOverflow = shown.length + 1 < names.length;
      const reserve = needsOverflow ? overflowPillPx : 0;
      if (pillPx + reserve > remaining) break;
      shown.push(name);
      used += pillPx;
    }
    return { shown, hasMore: shown.length < names.length };
  };

  const renderSortHeader = (col: SortKey, label: string) => (
    <span className={sortHeaderClass} onClick={() => handleSort(col)}>
      {label}
      {sortKey === col
        ? (sortDir === 'asc'
          ? <ArrowUpwardIcon style={{ fontSize: 13, marginLeft: 2, verticalAlign: 'middle' }} />
          : <ArrowDownwardIcon style={{ fontSize: 13, marginLeft: 2, verticalAlign: 'middle' }} />)
        : <UnfoldMoreIcon style={{ fontSize: 13, marginLeft: 2, verticalAlign: 'middle', opacity: 0.4 }} />}
    </span>
  );

  const columns: ListColumn<WidgetView>[] = [
    {
      label: renderSortHeader('live', ''),
      displayName: (w) => (
        <div style={{ display: 'flex', alignItems: 'flex-start', paddingTop: 3 }}>
          {w.live_status?.domain
            ? <Tooltip title={`Live on ${w.live_status.domain}`}>
                <FiberManualRecordIcon style={{ color: '#22c55e', fontSize: 11 }} />
              </Tooltip>
            : <FiberManualRecordIcon style={{ color: '#3a3a3a', fontSize: 11 }} />}
        </div>
      ),
      minWidth: 20,
    },
    {
      label: renderSortHeader('name', lang.name),
      onClick: (w) => onWidgetClick(w),
      displayName: (w) => (
        <div className={nameCellClass}>
          <div className={nameRowClass}>
            <span className={nameTextClass}>{w.name}</span>
          </div>
          {w.description ? <div className={descriptionClass}>{w.description}</div> : null}
        </div>
      ),
      minWidth: '22vw',
    },
    {
      label: renderTypeHeader(),
      displayName: (w) => {
        const t = getSourceType(w);
        return (
          <span className={t === 'collection' ? typeBadgeCollectionClass : typeBadgeProductClass}>
            {t === 'collection' ? lang.sourceType.collection : lang.sourceType.product}
          </span>
        );
      },
      minWidth: 90,
    },
    {
      label: lang.sources,
      onClick: (w) => onWidgetClick(w),
      displayName: (w) => {
        const names = getSourceNames(w);
        if (!names.length) return <span style={{ color: '#444', fontSize: 12 }}>—</span>;
        const maxLines = w.description ? 2 : 1;
        const { shown, hasMore } = slicePills(names, maxLines);
        return (
          <div className={sourcesCellClass}>
            {shown.map((name) => (
              <span key={name} className={sourcePillClass}>{name}</span>
            ))}
            {hasMore ? <span className={sourcePillOverflowClass}>•••</span> : null}
          </div>
        );
      },
      minWidth: '22vw',
    },
    {
      label: renderSortHeader('creator', lang.creator),
      onClick: (w) => onWidgetClick(w),
      displayName: (w) => w.created_by.displayName || 'unknown',
      minWidth: 80,
    },
    {
      label: '',
      displayName: (w) => {
        const isOwner = w.created_by._id === user?._id;
        return (
          <span
            onClick={(e) => { e.stopPropagation(); onWidgetLockClick(w); }}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: isOwner ? 'pointer' : 'not-allowed' }}
          >
            <Tooltip title={
              isOwner
                ? (w.is_locked ? lang.clickToUnlock : lang.clickToLock)
                : (w.is_locked ? lang.lockedNotOwner : '')
            }>
              <LockIcon className={w.is_locked ? undefined : lockDimClass} style={{
                fontSize: 18,
                color: w.is_locked ? '#ccc' : undefined,
              }} />
            </Tooltip>
          </span>
        );
      },
      minWidth: 36,
    },
    {
      label: '',
      displayName: (w) => (
        <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {w.is_locked ? (
            <Tooltip title={lang.deleteLocked}>
              <span style={{ display: 'flex' }}>
                <DeleteIcon style={{ color: '#444', cursor: 'not-allowed', fontSize: 18 }} />
              </span>
            </Tooltip>
          ) : (
            <DeleteIcon
              className={deleteClass}
              onClick={(e) => { e.stopPropagation(); onRemoveWidget(w); }}
              style={{ fontSize: 18 }}
            />
          )}
        </span>
      ),
    },
  ];

  return (
    <AuthorizedPage tab="widget" user={user}>
      <div
        className={pageClass}
        style={{ height: windowHeight, display: 'flex', flexDirection: 'column', backgroundColor: ADALONG_COLORS.SMOOTH_BLACK }}
      >
        <div style={{ backgroundColor: ADALONG_COLORS.DARK_BLACK }}>
          <NavBar active={Pathname.widget} />
        </div>
        <div className={bodyClass} style={{ flex: 1 }}>
          <div className={toolbarClass}>
            <div style={{ flex: 1 }} />
            <div className={createButtonsClass}>
              <ActionButton
                variant="blue"
                onAction={() => browsingService.goToPage(`${Pathname.widget}/${PathAction.create}`)}
              >
                {lang.createWidget}
              </ActionButton>
            </div>
          </div>

          <TableList
            list={displayedWidgets}
            columns={columns}
            emptyMessage={lang.noWidget}
            loading={isLoading}
          />

          <Hide hide={!errorLoading || !errorRemoving || !errorLocking}>
            <FormError error={errorLoading ? lang.error.refresh : lang.error.unknown} />
          </Hide>
        </div>
        <Footer />
      </div>
    </AuthorizedPage>
  );
}

const lang = LocalizedStrings({
  en: {
    createWidget: 'Create new widget',
    name: 'Name',
    type: 'Type',
    sources: 'Sources',
    creator: 'Created by',
    sourceType: {
      product: 'Product',
      collection: 'Collection',
    },
    clickToLock: 'Click to lock',
    clickToUnlock: 'Click to unlock',
    lockedNotOwner: 'Locked (you are not the owner)',
    deleteLocked: 'The widget must be unlocked before it can be deleted',
    confirmDelete: 'Deleting this widget is permanent and cannot be undone.',
    confirmUnlockWidget: 'Are you sure you want to unlock this widget?',
    noWidget: 'No widget found. Create one to start.',
    unlock: 'Unlock',
    cancel: 'Cancel',
    delete: 'Delete',
    error: {
      refresh: 'Unable to get widget list. Please try again or contact us.',
      unknown: 'An error occurred while removing the widget, please try again or contact us.',
    },
  },
});

const pageClass = style({
  display: 'flex',
  flexDirection: 'column',
});

const bodyClass = style({
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
});

const toolbarClass = style({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '18px 24px 8px',
  flexShrink: 0,
});

const createButtonsClass = style({
  display: 'flex',
  gap: 12,
});

const sortHeaderClass = style({
  display: 'inline-flex',
  alignItems: 'center',
  cursor: 'pointer',
  userSelect: 'none',
  $nest: {
    '&:hover': {
      color: '#fff',
    },
  },
});

const nameCellClass = style({
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
});

const nameRowClass = style({
  display: 'flex',
  alignItems: 'center',
});

const nameTextClass = style({
  fontWeight: 600,
  color: '#fff',
});

const descriptionClass = style({
  fontSize: 14,
  color: '#888',
  maxWidth: '22vw',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  marginTop: 2,
});

const baseBadgeStyle = {
  padding: '2px 8px',
  borderRadius: 10,
  fontSize: 11,
  fontWeight: 700 as const,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.05em',
  display: 'inline-block',
};

const typeBadgeCollectionClass = style({
  ...baseBadgeStyle,
  background: ADALONG_COLORS.ADALONG_YELLOW,
  color: '#3a4a00',
});

const typeBadgeProductClass = style({
  ...baseBadgeStyle,
  background: ADALONG_COLORS.ADALONG_GREEN,
  color: '#005530',
});

const sourcesCellClass = style({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '4px 8px',
  maxWidth: '22vw',
});

const sourcePillClass = style({
  display: 'inline-block',
  background: 'rgba(255,255,255,0.09)',
  borderRadius: 999,
  padding: '3px 10px',
  fontSize: 12,
  color: ADALONG_COLORS.SMOOTH_WHITE,
  whiteSpace: 'nowrap',
  maxWidth: '18vw',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

const sourcePillOverflowClass = style({
  display: 'inline-block',
  background: 'rgba(255,255,255,0.05)',
  borderRadius: 999,
  padding: '3px 8px',
  fontSize: 11,
  color: ADALONG_COLORS.LIGHT_GRAY,
  whiteSpace: 'nowrap',
  letterSpacing: 1,
});

const deleteClass = style({
  cursor: 'pointer',
  transition: 'color 0.2s',
  $nest: {
    '&:hover': {
      color: 'white',
    },
  },
});

const lockDimClass = style({
  color: 'rgba(180,180,180,0.2)',
  transition: 'color 0.2s',
  $nest: {
    '&:hover': {
      color: 'rgba(180,180,180,0.6) !important',
    },
  },
});


export default Widgets;
